"use client";

import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@ui/components/popover";
import { cn } from "@ui/lib";
import {
	createContext,
	useContext,
	useEffect,
	useState,
	useSyncExternalStore,
} from "react";
import { TIME_ZONE_COOKIE, zoneLabel } from "./time-zone";

/**
 * One clock for the whole admin panel.
 *
 * Every date used to be formatted in whatever timezone the runtime happened to
 * be in, which is UTC on the server and the viewer's on the browser, and the
 * mismatch was papered over with `suppressHydrationWarning`. That attribute
 * tells React not to repair the node, so a server-rendered date kept the
 * server's UTC text for the life of the page while the same timestamp drawn
 * later by the client read correctly: one screen, two answers, and a value
 * that "changed on refresh" depending on which half you were looking at.
 *
 * So the timezone is decided once per request, from a cookie, and every
 * timestamp is formatted IN it on both sides. The strings match, there is
 * nothing to repair, and nothing depends on where the person reading it
 * happens to be sitting.
 */

const TimeZoneContext = createContext<string>("UTC");

export function TimeZoneProvider({
	timeZone,
	children,
}: {
	timeZone: string;
	children: React.ReactNode;
}) {
	// Server-rendered in the zone the cookie asked for. On a first visit there
	// is no cookie, so the browser's own zone is written for next time and
	// adopted now, as an update after mount rather than a different first
	// render: React patches an update, and cannot patch a hydration it was
	// told to leave alone.
	const [zone, setZone] = useState(timeZone);
	useEffect(() => {
		setZone(timeZone);
		if (document.cookie.includes(`${TIME_ZONE_COOKIE}=`)) {
			return;
		}
		const detected = detectedZone();
		// biome-ignore lint/suspicious/noDocumentCookie: the server reads this on the next request, and the Cookie Store API is not in Safari
		document.cookie = `${TIME_ZONE_COOKIE}=${encodeURIComponent(detected)}; path=/; max-age=31536000; samesite=lax`;
		setZone(detected);
	}, [timeZone]);

	return (
		<TimeZoneContext.Provider value={zone}>
			{children}
		</TimeZoneContext.Provider>
	);
}

/** Pins the panel to a zone from now on, on this device. */
export function rememberTimeZone(timeZone: string) {
	// biome-ignore lint/suspicious/noDocumentCookie: as above, SSR has to see it
	document.cookie = `${TIME_ZONE_COOKIE}=${encodeURIComponent(timeZone)}; path=/; max-age=31536000; samesite=lax`;
	window.location.reload();
}

export function useTimeZone(): string {
	return useContext(TimeZoneContext);
}

export function detectedZone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
	} catch {
		return "UTC";
	}
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/**
 * One formatter per locale, zone and options for the life of the page: building an
 * Intl.DateTimeFormat is slow, and every timestamp on a screen asks for the same few.
 */
function formatter(
	locale: string,
	timeZone: string,
	options: Intl.DateTimeFormatOptions,
) {
	const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
	const cached = formatters.get(key);
	if (cached) {
		return cached;
	}
	const built = new Intl.DateTimeFormat(locale, { timeZone, ...options });
	formatters.set(key, built);
	return built;
}

// en-US, for "Sep" over "Sept" and an upper-case AM/PM.
const parts = (
	date: Date,
	timeZone: string,
	options: Intl.DateTimeFormatOptions,
) => formatter("en-US", timeZone, options).format(date);

/** "1:20 PM" */
export const clock = (date: Date, timeZone: string) =>
	parts(date, timeZone, { hour: "numeric", minute: "2-digit", hour12: true });

/**
 * "22 Sep", or "22 Sep 2026". Composed rather than left to a locale: en-US
 * writes "Sep 22" and en-GB writes "Sept", and the panel says "22 Sep".
 */
export function dateLabel(date: Date, timeZone: string, withYear: boolean) {
	const d = parts(date, timeZone, { day: "numeric" });
	const mon = parts(date, timeZone, { month: "short" });
	const year = withYear
		? ` ${parts(date, timeZone, { year: "numeric" })}`
		: "";
	return `${d} ${mon}${year}`;
}

/**
 * Calendar days between two instants, as that timezone counts them. Keyed
 * through en-CA, the one locale that writes a sortable YYYY-MM-DD.
 */
function daysApart(from: Date, to: Date, timeZone: string): number {
	const asUtc = (d: Date) => {
		const [yyyy, mm, dd] = formatter("en-CA", timeZone, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		})
			.format(d)
			.split("-");
		return Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd));
	};
	return Math.round((asUtc(to) - asUtc(from)) / 86_400_000);
}

/**
 * When something happened, in as few words as it takes to be unambiguous:
 * "1:20 PM" today, "Yesterday 9:16 PM", "Mon 9:16 PM" this week, then the date.
 */
export function when(date: Date, now: Date, timeZone: string): string {
	const days = daysApart(date, now, timeZone);
	if (days === 0) {
		return clock(date, timeZone);
	}
	if (days === 1) {
		return `Yesterday ${clock(date, timeZone)}`;
	}
	if (days > 1 && days < 7) {
		return `${parts(date, timeZone, { weekday: "short" })} ${clock(date, timeZone)}`;
	}
	const sameYear =
		parts(date, timeZone, { year: "numeric" }) ===
		parts(now, timeZone, { year: "numeric" });
	return `${dateLabel(date, timeZone, !sameYear)}, ${clock(date, timeZone)}`;
}

/** Calendar days between two instants, in the panel's timezone. */
export const calendarDaysApart = (from: Date, to: Date, timeZone: string) =>
	daysApart(from, to, timeZone);

/** The separator above a day of messages: "Sunday 20 September". */
export const dayHeading = (date: Date, timeZone: string) =>
	`${parts(date, timeZone, { weekday: "long" })} ${parts(date, timeZone, { day: "numeric" })} ${parts(date, timeZone, { month: "long" })}`;

/** Whether two instants fall on the same day, in the panel's timezone. */
export const sameDay = (a: Date, b: Date, timeZone: string) =>
	daysApart(a, b, timeZone) === 0;

/** "just now", "12m ago", then the calendar label: an age reads as recent for an hour. */
export function ago(date: Date, now: Date, timeZone: string): string {
	const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
	if (seconds < 0) {
		return when(date, now, timeZone);
	}
	if (seconds < 60) {
		return "just now";
	}
	if (seconds < 3600) {
		return `${Math.floor(seconds / 60)}m ago`;
	}
	return when(date, now, timeZone);
}

export function fullTime(date: Date, timeZone: string): string {
	return `${parts(date, timeZone, { weekday: "long" })} ${parts(date, timeZone, { day: "numeric" })} ${parts(date, timeZone, { month: "long" })} ${parts(date, timeZone, { year: "numeric" })} at ${parts(
		date,
		timeZone,
		{
			hour: "numeric",
			minute: "2-digit",
			second: "2-digit",
			hour12: true,
		},
	)} (${zoneLabel(timeZone)})`;
}

/** Minutes and hours of a conversation or a call: "4 min", "1 h 12 min". */
export function formatDuration(ms: number): string {
	const minutes = Math.round(ms / 60_000);
	if (minutes < 1) {
		return "under a minute";
	}
	if (minutes < 60) {
		return `${minutes} min`;
	}
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/**
 * A ticking clock for the relative labels. Unlike the formatting, "12m ago"
 * genuinely depends on now, so it is rendered from a fixed point on the server
 * and starts ticking once the page is live.
 */
const TICK_MS = 30_000;
const listeners = new Set<() => void>();
let nowMs = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(onChange: () => void) {
	listeners.add(onChange);
	timer ??= setInterval(() => {
		nowMs = Date.now();
		for (const listener of listeners) {
			listener();
		}
	}, TICK_MS);
	return () => {
		listeners.delete(onChange);
		if (listeners.size === 0 && timer) {
			clearInterval(timer);
			timer = null;
		}
	};
}

/**
 * 0 until the page is live: the server and the hydrating client agree on it,
 * so a relative label renders as the full "22 Sep, 1:20 PM" on both and only
 * becomes "12m ago" once the clock starts. Nothing mismatches, so nothing has
 * to be suppressed.
 */
export function useNow(): number {
	return useSyncExternalStore(
		subscribe,
		() => nowMs,
		() => 0,
	);
}

/**
 * A timestamp in the panel's timezone, with the full date, time and zone on
 * hover. `as="relative"` is for the newest thing on a screen, `full` for a
 * details row.
 */
export function Time({
	at,
	as = "short",
	label,
	className,
}: {
	at: Date;
	as?: "short" | "relative" | "full";
	/** Names what the timestamp is, on hover: "Last message", "Started". */
	label?: string;
	className?: string;
}) {
	const timeZone = useTimeZone();
	const now = new Date(useNow());
	const title = fullTime(at, timeZone);
	return (
		<time
			dateTime={at.toISOString()}
			title={label ? `${label}: ${title}` : title}
			className={className}
		>
			{as === "relative"
				? ago(at, now, timeZone)
				: as === "full"
					? title
					: when(at, now, timeZone)}
		</time>
	);
}

/**
 * Which clock the screen is speaking, and how to change it. Without this the
 * panel is quietly formatting in one zone while the person reads it in
 * another, which is how a timestamp becomes an argument.
 */
export function TimeZoneNote({ className }: { className?: string }) {
	const timeZone = useTimeZone();
	const [detected, setDetected] = useState<string | null>(null);
	useEffect(() => setDetected(detectedZone()), []);
	const choices = [
		...(detected && detected !== timeZone
			? [{ zone: detected, label: `Your time (${zoneLabel(detected)})` }]
			: []),
		...OTHER_ZONES.filter((z) => z !== timeZone && z !== detected).map(
			(zone) => ({ zone, label: zoneLabel(zone) }),
		),
	];

	return (
		<Popover>
			<PopoverTrigger
				render={
					<button
						type="button"
						className={cn(
							"rounded-md text-muted-foreground text-xs outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30",
							className,
						)}
					/>
				}
			>
				Times in {zoneLabel(timeZone)}
			</PopoverTrigger>
			<PopoverContent align="end" className="w-56 gap-0 p-1.5">
				<p className="px-2 pt-1.5 pb-1 font-medium text-muted-foreground text-xs">
					Show times in
				</p>
				{choices.map((choice) => (
					<button
						key={choice.zone}
						type="button"
						onClick={() => rememberTimeZone(choice.zone)}
						className="flex w-full items-center rounded-xl px-2 py-1.5 text-left text-sm outline-none hover:bg-muted focus-visible:bg-muted"
					>
						{choice.label}
					</button>
				))}
			</PopoverContent>
		</Popover>
	);
}

/** Enough to cover where this is read from, plus UTC for anyone comparing logs. */
const OTHER_ZONES = [
	"Asia/Beirut",
	"Asia/Dubai",
	"Asia/Riyadh",
	"Europe/Paris",
	"UTC",
];

export { safeZone, TIME_ZONE_COOKIE, zoneLabel } from "./time-zone";
